import * as React from "react";
import { observer } from "mobx-react-lite";
import { useTasksStore } from "../stores/context";
import {
    TaskList,
    TaskListHeaderControls,
    TaskPreviewDialog,
    TaskCreateDialog,
    TasksAssignDialog,
    TaskSendViaEmailDialog,
} from "../components";
import { useStore } from "../../common/stores";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import TabsContentContainer from "../../common/components/TabsContentContainer";
import { TaskService } from "../services";
import { TaskViewVisualStore } from "../stores";
import { LoadingIndicator } from "../../common/components/LoadingIndicator";
import ClientActionRunner from "../../administration/misc/ClientActionHandler";
import BulkTasksUpdateDialog from "../components/BulkTasksUpdateDialog";
import { useRef } from "react";
import { GlobalAdministrationService } from "../../administration/services/GlobalAdministrationService";

export const ALL_PROJECTS = "all_projects";
const taskService = new TaskService();
const adminService = new GlobalAdministrationService();
const TasksPage: React.FC = () => {
    const context = useTasksStore();
    const rootContext = useStore();
    const headerRef = useRef<HTMLDivElement>(null);

    const { taskId, widgetId, activityType } = useParams();
    const { userProfileStore } = useStore();
    const { tabsStore, errorStore, notificationStore } = rootContext;
    const projectsStore = rootContext.projectsStore;
    const templateStore = rootContext.templateStore;
    const taskViewVisualStore = context.taskPreviewVisualStore;
    const tasksGridVisualStore = context.tasksGridVisualStore;
    const tasksFilterVisualStore = context.tasksFiltersVisualStore;
    const tasksRootStore = context.tasksRootStore;

    const [searchParams] = useSearchParams();
    const uuid = searchParams.get("uuid");
    const [uuidSearchInProgress, setUuidSearchInProgress] =
        React.useState(true);

    React.useEffect(() => {
        if (uuid) {
            setUuidSearchInProgress(true);
        }
    }, [uuid]);

    const openTaskFullView = React.useCallback(
        async (id: string) => {
            tasksRootStore.removeSubscriptionsFromsGlobalViewStore();
            if (!tabsStore.taskStores[id]) {
                const actionRunner = new ClientActionRunner();
                tabsStore.addTaskStore(
                    new TaskViewVisualStore(
                        taskService,
                        tabsStore,
                        errorStore,
                        userProfileStore,
                        notificationStore,
                        tasksRootStore,
                        actionRunner,
                    ),
                    id,
                );
            }
            try {
                await tabsStore.taskStores[id].expandTask(id);
            } finally {
                tasksRootStore.setPageIsLoading(false);
            }
        },
        [
            tabsStore,
            taskService,
            errorStore,
            userProfileStore,
            notificationStore,
            tasksRootStore,
        ],
    );

    React.useEffect(() => {
        const handleUuidSearch = async () => {
            const urlProjectId = searchParams.get("projectId");
            const uuid = searchParams.get("uuid");

            if (!uuid || !urlProjectId) {
                setUuidSearchInProgress(false);
                return;
            }
            if (!userProfileStore.userProfile) {
                return;
            }

            try {
                tasksRootStore.setPageIsLoading(true);

                const metadataResult =
                    await adminService.getMetadataDefinitionsForProject(
                        urlProjectId,
                    );

                if (metadataResult.isErr()) {
                    errorStore.addError(metadataResult.error.data);
                    tasksRootStore.setPageIsLoading(false);
                    setUuidSearchInProgress(false);
                    return;
                }

                const uuidField = metadataResult.value?.find(
                    (m) => m.name.toLowerCase() === "uuid",
                );

                if (!uuidField) {
                    tasksRootStore.setPageIsLoading(false);
                    setUuidSearchInProgress(false);
                    return;
                }

                const result = await taskService.searchTasksByUuid(
                    urlProjectId,
                    uuid,
                    uuidField.id,
                );

                if (result.isErr()) {
                    errorStore.addError(result.error.data);
                    tasksRootStore.setPageIsLoading(false);
                    setUuidSearchInProgress(false);
                    return;
                }

                const tasks = result.value?.tasks || [];

                if (tasks.length === 1) {
                    await openTaskFullView(tasks[0].id);
                    setUuidSearchInProgress(false);
                } else {
                    tasksGridVisualStore.setSelectedProject(urlProjectId);

                    // Apply UUID filter
                    const uuidFilter = {
                        entityReference: uuidField.id,
                        operator: "Equal",
                        value: [uuid],
                        extraFilters: {},
                        fieldType: "Metadata" as const,
                        isWidgetFilter: false,
                    };

                    tasksFilterVisualStore.columnsFilterDict[uuidField.id] =
                        uuidFilter;
                    await tasksRootStore.updateColumnFilters(
                        urlProjectId,
                        tasksFilterVisualStore.prepareRequestFilters(),
                    );
                    tasksRootStore.setPageIsLoading(false);
                    setUuidSearchInProgress(false);
                }
            } catch (error) {
                console.error("Error during UUID search:", error);
                setUuidSearchInProgress(false);
            }
        };

        handleUuidSearch();
    }, [
        searchParams,
        userProfileStore.userProfile,
        errorStore,
        tasksRootStore,
        tasksGridVisualStore,
        tasksFilterVisualStore,
        adminService,
        taskService,
        openTaskFullView,
    ]);

    React.useEffect(() => {
        if (taskId && userProfileStore.userProfile) {
            openTaskFullView(taskId);
        }
    }, [
        taskId,
        widgetId,
        activityType,
        userProfileStore.userProfile,
        openTaskFullView,
    ]);

    const singleUuidTaskId = uuid
        ? Object.keys(tabsStore.taskStores).find(
              (id) => tabsStore.taskStores[id]?.taskPreview,
          )
        : undefined;

    // Return task full view if taskId is in URL and loaded, or if a single task was found via UUID search
    if (
        !tasksRootStore.pageIsLoading &&
        ((taskId && tabsStore.taskStores[taskId]?.taskPreview) ||
            (uuid &&
                singleUuidTaskId &&
                tabsStore.taskStores[singleUuidTaskId]?.taskPreview))
    ) {
        return (
            <>
                <TabsContentContainer />
                <TaskCreateDialog
                    store={context.taskCreateVisualStore}
                    templateStore={templateStore}
                />
            </>
        );
    }

    if (taskId && tasksRootStore.pageIsLoading) {
        return <LoadingIndicator />;
    }

    if (uuidSearchInProgress) {
        return <LoadingIndicator />;
    }

    return (
        <div style={{ userSelect: "none" }}>
            <TaskSendViaEmailDialog
                store={taskViewVisualStore}
                visible={taskViewVisualStore.emailTaskToUserDialogVisible}
                setVisible={taskViewVisualStore.setEmailTaskToUserDialogVisible}
                isSending={taskViewVisualStore.sendingEmailToUsers}
                sendEmails={taskViewVisualStore.emailTaskToUsers}
            />
            <TasksAssignDialog store={tasksGridVisualStore} />
            <TaskCreateDialog
                store={context.taskCreateVisualStore}
                templateStore={templateStore}
            />
            {tasksGridVisualStore.isBulkUpdateDialogOpen ? (
                <BulkTasksUpdateDialog store={tasksGridVisualStore} />
            ) : null}
            <div ref={headerRef}>
                <TaskListHeaderControls
                    store={tasksGridVisualStore}
                    filterStore={tasksFilterVisualStore}
                    createStore={context.taskCreateVisualStore}
                    projectsStore={projectsStore}
                />
            </div>
            <div
                className="tasks-content-container"
                style={{
                    transition: ".3s",
                }}
            >
                <TaskList
                    store={tasksGridVisualStore}
                    taskPreviewStore={taskViewVisualStore}
                    headerRef={headerRef}
                />
                <TaskPreviewDialog headerRef={headerRef} />
            </div>
        </div>
    );
};

export default observer(TasksPage);
